import { useState } from "react";
import { Search, Link2, Trash2, VideoOff, ChevronsUpDown, Check, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useSignedMediaUrls } from "@/lib/media/signedMediaUrl";

export interface VideoAsset {
  id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  mime_type: string | null;
  assigned_lesson_id: string | null;
  assigned_course_name: string | null;
  match_confidence: string;
  status: string;
  created_at: string;
}

interface LessonOption {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
}

interface VideoAssetGridProps {
  assets: VideoAsset[];
  lessons: LessonOption[];
  courseNames: string[];
  onAssign: (assetId: string, lessonId: string, courseName: string) => void;
  onUnassign: (assetId: string) => void;
  onDelete: (assetId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  assigned: "bg-accent/15 text-accent border-accent/30",
  unassigned: "bg-muted text-muted-foreground border-border",
  "needs-review": "bg-secondary/15 text-secondary border-secondary/30",
};

function formatBytes(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function VideoAssetGrid({
  assets,
  lessons,
  courseNames,
  onAssign,
  onUnassign,
  onDelete,
}: VideoAssetGridProps) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");

  const signedUrls = useSignedMediaUrls(assets.map((a) => a.file_url));

  const filtered = assets.filter((asset) => {
    const matchSearch =
      !search ||
      asset.file_name.toLowerCase().includes(search.toLowerCase()) ||
      (asset.assigned_course_name || "").toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || asset.status === filter;
    const matchCourse = courseFilter === "all" || courseFilter === "unassigned"
      ? courseFilter === "all" || !asset.assigned_course_name
      : asset.assigned_course_name === courseFilter;

    return matchSearch && matchFilter && matchCourse;
  });

  const lessonsByCourse = lessons.reduce<Record<string, LessonOption[]>>((acc, lesson) => {
    (acc[lesson.course_name] = acc[lesson.course_name] || []).push(lesson);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by filename or course..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-card border-border"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-card border-border">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            <SelectItem value="needs-review">Needs Review</SelectItem>
          </SelectContent>
        </Select>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-52 bg-card border-border">
            <SelectValue placeholder="Course" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All Courses</SelectItem>
            <SelectItem value="unassigned">No Course Assigned</SelectItem>
            {courseNames.map((name) => (
              <SelectItem key={name} value={name} className="text-xs">
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 text-xs font-body text-muted-foreground">
        <span>{filtered.length} of {assets.length} shown</span>
        <span>•</span>
        <span>{assets.filter((asset) => asset.status === "assigned").length} assigned</span>
        <span>•</span>
        <span>{assets.filter((asset) => asset.status === "unassigned").length} unassigned</span>
        <span>•</span>
        <span>{assets.filter((asset) => asset.status === "needs-review").length} needs review</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <VideoOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-body">No video assets found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((asset) => {
            const assignedLesson = asset.assigned_lesson_id
              ? lessons.find((lesson) => lesson.lesson_id === asset.assigned_lesson_id)
              : null;

            return (
              <div key={asset.id} className="rounded-xl border border-border bg-card overflow-hidden group">
                <div className="w-full aspect-[16/10] bg-muted/30 relative overflow-hidden">
                  <video
                    src={signedUrls[asset.file_url] ?? undefined}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-background/30">
                    <div className="w-10 h-10 rounded-full bg-background/80 border border-border flex items-center justify-center">
                      <Play className="w-4 h-4 text-foreground fill-current ml-0.5" />
                    </div>
                  </div>
                </div>

                <div className="p-3 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-heading font-medium text-foreground truncate flex-1" title={asset.file_name}>
                      {asset.file_name}
                    </p>
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[asset.status] || ""}`}>
                      {asset.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-body">
                    <span>{formatBytes(asset.file_size)}</span>
                    <span>•</span>
                    <span>{asset.mime_type?.split("/")[1]?.toUpperCase() || "—"}</span>
                    <span>•</span>
                    <span>{new Date(asset.created_at).toLocaleDateString()}</span>
                  </div>

                  {assignedLesson && (
                    <div className="text-[10px] text-accent font-body truncate">
                      ✓ {assignedLesson.course_name} → {assignedLesson.lesson_name}
                    </div>
                  )}

                  <LessonCombobox
                    value={asset.assigned_lesson_id || null}
                    lessonsByCourse={lessonsByCourse}
                    onSelect={(value) => {
                      if (!value) {
                        onUnassign(asset.id);
                      } else {
                        const lesson = lessons.find((item) => item.lesson_id === value);
                        if (lesson) onAssign(asset.id, lesson.lesson_id, lesson.course_name);
                      }
                    }}
                  />

                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground hover:text-foreground flex-1"
                      onClick={() => window.open(signedUrls[asset.file_url] ?? asset.file_url, "_blank", "noopener,noreferrer")}
                    >
                      <Play className="w-3 h-3 mr-1" /> Open
                    </Button>
                    {assignedLesson && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-muted-foreground hover:text-foreground flex-1"
                        onClick={() => window.open(`/lesson/${assignedLesson.lesson_id}`, "_blank")}
                      >
                        <Link2 className="w-3 h-3 mr-1" /> Lesson
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onDelete(asset.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface LessonComboboxProps {
  value: string | null;
  lessonsByCourse: Record<string, LessonOption[]>;
  onSelect: (lessonId: string | null) => void;
}

function LessonCombobox({ value, lessonsByCourse, onSelect }: LessonComboboxProps) {
  const [open, setOpen] = useState(false);

  let currentLabel = "Assign to lesson...";
  if (value) {
    for (const courseLessons of Object.values(lessonsByCourse)) {
      const found = courseLessons.find((lesson) => lesson.lesson_id === value);
      if (found) {
        currentLabel = found.lesson_name;
        break;
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between text-xs bg-muted/30 border-border font-normal"
        >
          <span className="truncate">{currentLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search lessons..." className="text-xs h-9" />
          <CommandList className="max-h-60">
            <CommandEmpty className="py-3 text-xs text-center text-muted-foreground">No lesson found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__unassigned__"
                onSelect={() => {
                  onSelect(null);
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check className={cn("mr-2 h-3 w-3", !value ? "opacity-100" : "opacity-0")} />
                — Unassigned —
              </CommandItem>
            </CommandGroup>
            {Object.entries(lessonsByCourse).map(([course, courseLessons]) => (
              <CommandGroup
                key={course}
                heading={course}
                className="[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:font-heading"
              >
                {courseLessons.map((lesson) => (
                  <CommandItem
                    key={lesson.lesson_id}
                    value={`${course} ${lesson.lesson_name}`}
                    onSelect={() => {
                      onSelect(lesson.lesson_id);
                      setOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check className={cn("mr-2 h-3 w-3", value === lesson.lesson_id ? "opacity-100" : "opacity-0")} />
                    {lesson.lesson_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
